import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import * as WizardClasses from '../WizardClasses'
import { DumbField } from '../Field'
import { TextInput, Select, DateInput } from './Form'
import ReactMarkdown from 'components/Markdown/Markdown'
import { noop } from 'utilities'
import format from 'date-fns/format'
import * as api from 'requests'

const SIGNATURE_CAPACITY_LABEL = process.env.SIGNATURE_CAPACITY_LABEL || "Signer's Capacity"

const getDefaultSignature = () => ({
  name: '',
  sig: '',
  date: process.env.SIGNATURE_DEFAULT_DATE ? format(new Date(), 'MM/dd/yyyy') : null,
  capacity: '',
  behalf: '',
})

const SignatureRow = ({
  label,
  value = {},
  placeholder,
  disabled,
  multiple,
  todayButton,
  showCapacity,
  showOnBehalfOf,
  capacityFreeText,
  signerCapacityTypes,
  onChange,
  index,
  srcField,
}) => {
  const bubbleByKey = useCallback((v, key) => {
    const updated = {
      ...value,
      [key]: v,
      ...(key === 'capacity' && (v === 'self' || v === '') ? { behalf: '' } : {}),
    }
    onChange(updated, index)
  }, [value, onChange, index])

  const bubbleSig = sig => onChange({ ...value, sig }, index)
  const bubbleDate = date => onChange({ ...value, date }, index)

  const getFriendlyLabel = (label) => (label || '').replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '-').replace(/^-+|-+$/g, '')

  value.name = getLabel

  const getLabel = useMemo(() => {
    let newLabel = multiple
    ? new WizardClasses[srcField.fieldClass]().getHumanReadableValue(
        srcField,
        label
      )
    : ''
  if (multiple && srcField.fieldClass === 'NameQualifiedOrg') {
    newLabel = new WizardClasses[srcField.fieldClass]().getHumanReadableSignatureValue(
      srcField,
      label
    )
  }
    return newLabel
}, [srcField, multiple, label])

  useEffect(() => {
    if (!value.capacity || value.capacity.toLowerCase() === 'self') {
      value.behalf = '';
    }
  }, [value.capacity])

  return (
    <div className="row signature-row">
      {multiple && !showCapacity && (
        <div className="col-sm-3 label">
          <span>{getLabel}</span>
        </div>
      )}

      {showCapacity && (
        <div className="col-sm-2">
          {capacityFreeText ? (
            <TextInput
              disabled={disabled}
              width="100%"
              value={value.capacity}
              onChange={v => bubbleByKey(v, 'capacity')}
              onBlur={noop}
              ariaLabel={multiple ? SIGNATURE_CAPACITY_LABEL + ' for ' + getLabel : SIGNATURE_CAPACITY_LABEL}
              fieldId={multiple ? 'signature-capacity-' + getFriendlyLabel(getLabel) : 'signature-capacity'}
            />
          ) : (
            <Select
              disabled={disabled}
              value={value.capacity}
              onChange={v => bubbleByKey(v, 'capacity')}
              onBlur={noop}
              options={[{ value: '', label: '[Select an Option]' }, ...signerCapacityTypes]}
              ariaLabel={multiple ? SIGNATURE_CAPACITY_LABEL + ' for ' + getLabel : SIGNATURE_CAPACITY_LABEL}
              fieldId={multiple ? 'signature-capacity-' + getFriendlyLabel(getLabel) : 'signature-capacity'}
            />
          )}
        </div>
      )}

      {showCapacity && showOnBehalfOf && (
        <div className={`col-sm-3 ${multiple ? 'label' : ''}`}>
          {multiple ? (
            <span>{getLabel}</span>
          ) : (
            <TextInput
              value={value.behalf}
              disabled={
                disabled || (!multiple && (!value.capacity || value.capacity.toLowerCase() === 'self'))
              }
              width="100%"
              placeholder={placeholder}
              onChange={v => bubbleByKey(v, 'behalf')}
              onBlur={noop}
              ariaLabel="Signing on behalf of"
              fieldId={multiple ? 'signature-behalf-' + getFriendlyLabel(getLabel) : 'signature-behalf'}
            />
          )}
        </div>
      )}

      <div className={`col-sm-${showCapacity ? (showOnBehalfOf ? '3' : '5') : multiple ? '5' : '7'}`}>
        <TextInput
          value={value.sig}
          disabled={disabled}
          width="100%"
          placeholder={placeholder}
          onChange={bubbleSig}
          onBlur={noop}
          ariaLabel={multiple ? `Signature for ${getLabel}` : 'Signature'}
          fieldId={multiple ? 'signature-' + getFriendlyLabel(getLabel) : 'signature'}
        />
      </div>

      <div className={`col-sm-4`}>
        <DateInput
          value={value.date}
          disabled={disabled || process.env.DISALLOW_SIGNATURE_DATE_CHANGE}
          todayButton={process.env.DISALLOW_SIGNATURE_DATE_CHANGE ? null : todayButton}
          onChange={bubbleDate}
          onBlur={noop}
          ariaLabel={multiple ? `Signature date for ${getLabel}` : 'Signature Date'}
          fieldId={multiple ? 'signature-date-' + getFriendlyLabel(getLabel) : 'signature-date'}
        />
      </div>
    </div>
  )
}

export const Signatures = (props) => {
  const {
    value = [],
    onChange,
    emptyText,
    description,
    placeholder,
    disabled,
    printMailRequired,
    correctionMode,
    multiple,
    required,
    todayButton,
    showCapacity,
    showOnBehalfOf,
    capacityFreeText,
    excludeDeletedRows,
    sourceTypeId
  } = props

  const srcRepeater = useSelector(state => state.wizard.wizard.fields[props.srcRepeater])
  const srcField = useSelector(state => state.wizard.wizard.fields[props.srcField])
  const [signerCapacityTypes, setSignerCapacityTypes] = useState([])

  const getValueArray = () => srcRepeater.value.map((_, i) => value[i] || getDefaultSignature())

  useEffect(() => {
    if (!capacityFreeText && showCapacity) {
      api.GET_SIGNER_CAPACITY_TYPES_BY_SOURCE_TYPE_ID(
        sourceTypeId.toString(),
        response => {
          response &&
            setSignerCapacityTypes(
              response.map(type => ({ label: type.Name, value: type.Name }))
            )
        },
        error => console.error(error)
      )
    }
  }, [capacityFreeText, showCapacity, sourceTypeId])

  const setSignatureValueByIndex = (val, idx) => {
    const rows = [...getValueArray()]
    rows[idx] = val
    onChange(rows, {})
  }

  const setSingleSignature = val => onChange([val], {})

  const getErrorMessageByIndex = index => {
    const errors = getErrors()
    if (!errors || errors.length <= index || (errors[index] && errors[index].bothValid)) return ''
    return [errors[index].errorMessage]
  }

  const getErrors = () => {
    if (props.errorMessage == '') return ''
    for (let i = 0; i < props.errorMessage.length; i++) {
      if (Array.isArray(props.errorMessage[i])) {
        return props.errorMessage[i]
      }
    }
  }

  const getOuterErrors = () => {
    let errorlist = []
    for (let i = 0; i < props.errorMessage.length; i++) {
      if (typeof props.errorMessage[i] === 'string') {
        errorlist.push(props.errorMessage[i])
        return errorlist
      }
    }
  } 

  return (
    <div className="panel signatures" role='grid'>
      {description && <ReactMarkdown source={description} className="markdown attestation" />}

      <div className="row" role='row'>
        {multiple && !showCapacity && <div className="field-label col-sm-3">Name</div>}
        {showCapacity && (
          <React.Fragment>
            <div className="field-label col-sm-2" role='columnheader'>
              <span className="label-text">
                {SIGNATURE_CAPACITY_LABEL}
                {required && <span className="required">*</span>}
              </span>
            </div>
            {showOnBehalfOf && (
              <div className="field-label col-sm-3" role='columnheader'>
                <span className="label-text">
                  On behalf of{required && <span className="required">*</span>}
                </span>
              </div>
            )}
          </React.Fragment>
        )}
        <div className={`field-label col-sm-${showCapacity ? (showOnBehalfOf ? '3' : '5') : multiple ? '5' : '7'}`} role='columnheader'>
          <span className="label-text">
            Signature{required && <span className="required">*</span>}
          </span>
        </div>
        <div className={`field-label col-sm-4`} role='columnheader'>
          <span className="label-text">
            Date{required && <span className="required">*</span>}
          </span>
        </div>
      </div>

      {multiple ? (
        srcRepeater && srcField ? (
          srcRepeater.value.length === 0 ? (
            <ReactMarkdown source={emptyText} className="markdown" />
          ) : (
            srcRepeater.value.filter(r => !excludeDeletedRows || r._statusId !== 3).map((row, i) => (
              <DumbField errorMessage={getErrorMessageByIndex(i)} key={i}>
                <SignatureRow
                  label={row[srcField.fieldId]}
                  disabled={
                    disabled ||
                    (process.env.ALLOW_E_SIGN_ON_CORRECTIONS && correctionMode ? false : printMailRequired)
                  }
                  placeholder={placeholder}
                  onChange={setSignatureValueByIndex}
                  value={value[i]}
                  srcField={srcField}
                  multiple={multiple}
                  todayButton={todayButton}
                  showCapacity={showCapacity}
                  showOnBehalfOf={showOnBehalfOf}
                  capacityFreeText={capacityFreeText}
                  signerCapacityTypes={signerCapacityTypes}
                  index={i}
                />
              </DumbField>
            ))
          )
        ) : (
          <span className="field-error">
            A Source Repeater and Source Field must be selected when requiring multiple signatures.
          </span>
        )
      ) : (
        <DumbField errorMessage={getErrorMessageByIndex(0)}>
          <SignatureRow
            label=""
            disabled={
              disabled || (process.env.ALLOW_E_SIGN_ON_CORRECTIONS && correctionMode ? false : printMailRequired)
            }
            placeholder={placeholder}
            onChange={setSingleSignature}
            value={value[0]}
            multiple={multiple}
            todayButton={todayButton}
            showCapacity={showCapacity}
            showOnBehalfOf={showOnBehalfOf}
            capacityFreeText={capacityFreeText}
            signerCapacityTypes={signerCapacityTypes}
            srcField={srcField}
            index={0}
          />
        </DumbField>
      )}

      {getOuterErrors() && getOuterErrors().length > 0 && getOuterErrors()[0] !== '' && (
        <DumbField errorMessage={getOuterErrors()} />
      )}
    </div>
  )
}

export default Signatures